/**
 * Single source of truth for the product size-matrix grids.
 *
 * Keyed by product slug, each entry holds the `sizeMatrixConfig` (drives the
 * SizeMatrixSelector grid) and the matching flat `sizes` array (kept in sync so
 * the standard product views and search stay consistent).
 *
 * Consumed by:
 *  - src/data/products.ts        (seed data)
 *  - src/services/firebase/products.service.ts (migrateSizeMatrixConfigs)
 */
import { SizeMatrixConfig } from '@/types/product.types';

export interface SizeMatrixSeed {
  sizeMatrixConfig: SizeMatrixConfig;
  sizes: string[];
}

const LENGTH_ROWS = [
  { id: '21mm', label: '21mm', notation: '21mm' },
  { id: '25mm', label: '25mm', notation: '25mm' },
];

export const SIZE_MATRIX_CONFIGS: Record<string, SizeMatrixSeed> = {
  'mani-k-files': {
    sizeMatrixConfig: {
      title: 'Select K-File Sizes',
      unitLabel: 'file',
      gridColumns: 1,
      columnPrefix: ' #',
      includeSectionInKey: false,
      showSectionHeader: false,
      sections: [
        {
          id: 'kf',
          label: 'K-Files',
          shortLabel: 'KF',
          rows: LENGTH_ROWS,
          columns: [8, 10, 15, 20, 25],
        },
      ],
    },
    sizes: [
      '21mm #8', '21mm #10', '21mm #15', '21mm #20', '21mm #25',
      '25mm #8', '25mm #10', '25mm #15', '25mm #20', '25mm #25',
    ],
  },

  'rotary-files-dxl-pro': {
    sizeMatrixConfig: {
      title: 'Select Rotary File Sizes',
      unitLabel: 'file',
      gridColumns: 1,
      columnPrefix: ' ',
      includeSectionInKey: false,
      showSectionHeader: true,
      sections: [
        {
          id: 'orifice',
          label: 'Orifice Opener',
          rows: [{ id: 'oo', label: 'Orifice Opener', notation: '8/17' }],
          columns: [''],
        },
        {
          id: 'length',
          label: 'Length',
          rows: LENGTH_ROWS,
          columns: ['2/19', '4/17', '4/20', '4/25', '4/30', '6/20', '6/25', '6/30'],
        },
      ],
    },
    sizes: [
      '8/17',
      '21mm 2/19', '21mm 4/17', '21mm 4/20', '21mm 4/25', '21mm 4/30', '21mm 6/20', '21mm 6/25', '21mm 6/30',
      '25mm 2/19', '25mm 4/17', '25mm 4/20', '25mm 4/25', '25mm 4/30', '25mm 6/20', '25mm 6/25', '25mm 6/30',
    ],
  },

  'c-plus-files': {
    sizeMatrixConfig: {
      title: 'Select C Plus File Sizes',
      unitLabel: 'file',
      gridColumns: 1,
      columnPrefix: ' ',
      includeSectionInKey: false,
      showSectionHeader: false,
      sections: [
        {
          id: 'cplus',
          label: 'C Plus Files',
          shortLabel: 'CF',
          rows: LENGTH_ROWS,
          columns: ['8', '10', '12.5'],
        },
      ],
    },
    sizes: ['21mm 8', '21mm 10', '21mm 12.5', '25mm 8', '25mm 10', '25mm 12.5'],
  },

  'dexell-niti-hand-files': {
    sizeMatrixConfig: {
      title: 'Select NITI Hand File Sizes',
      unitLabel: 'file',
      gridColumns: 1,
      columnPrefix: ' ',
      includeSectionInKey: false,
      showSectionHeader: false,
      sections: [
        {
          id: 'hand',
          label: 'NITI Hand Files',
          shortLabel: 'HF',
          rows: LENGTH_ROWS,
          columns: ['Assorted'],
        },
      ],
    },
    sizes: ['21mm Assorted', '25mm Assorted'],
  },

  'diadent-gutta-percha-points': {
    sizeMatrixConfig: {
      title: 'Select Point Sizes',
      unitLabel: 'point',
      gridColumns: 1,
      columnPrefix: '/',
      includeSectionInKey: false,
      showSectionHeader: false,
      sections: [
        {
          id: 'gp',
          label: 'Gutta Percha',
          shortLabel: 'GP',
          rows: [
            { id: '4', label: '4', notation: '4' },
            { id: '6', label: '6', notation: '6' },
          ],
          columns: [20, 25],
        },
      ],
    },
    sizes: ['4/20', '4/25', '6/20', '6/25'],
  },
};
